import { MutationCache, MutationKey } from "@tanstack/react-query";
import { useEffect } from "react";

// Subscription lists are heterogeneous (one entry per mutation key, each with
// its own variable/data types), so storage is typed with `unknown` and the
// generic hooks cast their typed callback to the storage shape at the push site.
type OnMutateCallback = (variables: unknown) => void;
type OnSuccessCallback = (data: unknown, variables: unknown, context: unknown) => void;
type OnErrorCallback = (error: unknown, variables: unknown, context: unknown) => void;

const onMutationStartSubscriptions: { mutationKey: MutationKey; callback: OnMutateCallback }[] = [];
const onMutationSuccessSubscriptions: { mutationKey: MutationKey; callback: OnSuccessCallback }[] = [];
const onMutationErrorSubscriptions: { mutationKey: MutationKey; callback: OnErrorCallback }[] = [];

export const useMutationStartCallback = <T>(mutationKey: MutationKey, callback: (variables: T) => void) => {
  useEffect(() => {
    const entry = { mutationKey, callback: callback as OnMutateCallback };
    onMutationStartSubscriptions.push(entry);
    return () => {
      const index = onMutationStartSubscriptions.findIndex((subscription) => subscription === entry);
      if (index !== -1) {
        onMutationStartSubscriptions.splice(index, 1);
      }
    };
  }, [mutationKey, callback]);
};

export const useMutationSuccessCallback = <TData, TVars>(
  mutationKey: MutationKey,
  callback: (data: TData, variables: TVars, context: unknown) => void,
) => {
  useEffect(() => {
    const entry = { mutationKey, callback: callback as OnSuccessCallback };
    onMutationSuccessSubscriptions.push(entry);
    return () => {
      const index = onMutationSuccessSubscriptions.findIndex((subscription) => subscription === entry);
      if (index !== -1) {
        onMutationSuccessSubscriptions.splice(index, 1);
      }
    };
  }, [mutationKey, callback]);
};

export const useMutationErrorCallback = <TVars>(
  mutationKey: MutationKey,
  callback: (error: unknown, variables: TVars, context: unknown) => void,
) => {
  useEffect(() => {
    const entry = { mutationKey, callback: callback as OnErrorCallback };
    onMutationErrorSubscriptions.push(entry);
    return () => {
      const index = onMutationErrorSubscriptions.findIndex((subscription) => subscription === entry);
      if (index !== -1) {
        onMutationErrorSubscriptions.splice(index, 1);
      }
    };
  }, [mutationKey, callback]);
};

function isEqualMutationKey(a?: MutationKey, b?: MutationKey): boolean {
  if (!a || !b) {
    return a === b;
  }

  return a.length === b.length && a.every((val, index) => val === b[index]);
}

export const mutationCache = new MutationCache({
  onMutate: (variables, mutation) => {
    const callbacks = onMutationStartSubscriptions
      .filter(({ mutationKey }) => isEqualMutationKey(mutation.options.mutationKey, mutationKey))
      .map(({ callback }) => callback);
    callbacks.forEach((callback) => callback(variables));
  },
  onSuccess: (data, variables, context, mutation) => {
    const callbacks = onMutationSuccessSubscriptions
      .filter(({ mutationKey }) => isEqualMutationKey(mutation.options.mutationKey, mutationKey))
      .map(({ callback }) => callback);
    callbacks.forEach((callback) => callback(data, variables, context));
  },
  onError: (error, variables, context, mutation) => {
    const callbacks = onMutationErrorSubscriptions
      .filter(({ mutationKey }) => isEqualMutationKey(mutation.options.mutationKey, mutationKey))
      .map(({ callback }) => callback);
    callbacks.forEach((callback) => callback(error, variables, context));
  },
});
